/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { createContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import { PublisherInterface } from '@chili-publish/publisher-interface';

import { useArtworkDrawers } from 'hooks/useArtworkDrawers';
import { getFileUriSas, getExportedFile } from 'store/slices/files';
import {
  downloadArtworkPdf,
  exportDocument,
  refreshDocument,
  saveDocument,
  toggleLoadingDocument,
  toggleSavingArtwork,
} from 'store/slices/artworks';

export const ArtworkContext = createContext(null);

export const ArtworkProvider = ({ children, ...props }) => {
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();
  const { openDrawerArtworkFiles, closeDrawerArtworkFiles } = useArtworkDrawers();

  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [documentRendered, setDocumentRendered] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [loadingSelectImage, setLoadingSelectImage] = useState(false);
  const [publisher, setPublisher] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [templateLayout, setTemplateLayout] = useState(null);

  const [chiliDoc, setChiliDoc] = useState(null);
  const [chiliSelectedFrame, setChiliSelectedFrame] = useState(null); //check if better if this is just fetched on the pages its used
  const [showEditorTools, setShowEditorTools] = useState(null);
  const [chiliSelectedText, setChiliSelectedText] = useState(null);

  const initializePublisher = async (iframeRef) => {
    try {
      const iframe = iframeRef.current;
      if (iframe) {
        const publisher = await PublisherInterface.build(iframe);

        publisher
          .addListener('DocumentFullyLoaded', () => {
            publisher.addListener('DocumentFullyRendered', async () => {
              console.log('document is rendered');
              dispatch(toggleLoadingDocument(false)); //check if can be removed
              setDocumentRendered(true);

              await publisher.executeFunction('document.editor', 'Fit', 'page');
              const doc = await publisher.getObject('document');
              await publisher.setProperty('document', 'zoom', doc.zoom);
              setChiliDoc(doc);
            });
            publisher.addListener('SelectedFrameChanged', async () => {
              const selectedFrame = await publisher.getObject('document.selectedFrame');
              setChiliSelectedFrame(selectedFrame);
              if (selectedFrame && selectedFrame?.type === 'image') {
                openDrawerArtworkFiles();
              } else closeDrawerArtworkFiles();
            });
            publisher.addListener('UndoStackChanged', async () => {
              const doc = await publisher.getObject('document');
              setChiliDoc(doc);
            });
            publisher.addListener('FrameRotated', async () => {
              const frame = await publisher.getObject('document.selectedFrame');
              setChiliSelectedFrame(frame);
            });
            publisher.addListener('FrameResized', async () => {
              const frame = await publisher.getObject('document.selectedFrame');
              setChiliSelectedFrame(frame);
            });
            publisher.addListener('FrameMoved', async () => {
              const frame = await publisher.getObject('document.selectedFrame');
              setChiliSelectedFrame(frame);
            });
            publisher.addListener('TextSelectionChanged', async () => {
              const doc = await publisher.getObject('document');
              if (doc.selectedText) {
                const selectedText = await publisher.getObject('document.selectedText.textFormat');
                setChiliSelectedText(selectedText);
              } else setChiliSelectedText(null);
            });
          })
          .then(() => {
            enqueueSnackbar('Document Loaded', { autoHideDuration: 3000 });
            setPublisher(publisher);
          })
          .catch((error) => {
            console.log(error);
            enqueueSnackbar('Error loading document', { variant: 'error' });
          });
      }
    } catch (error) {
      console.log('Failed to initialize publisher interface:', error);
      enqueueSnackbar('Error loading publisher', { variant: 'error' });
    }
  };

  const initializeChiliDoc = async () => {
    if (!publisher) return;
    const doc = await publisher.getObject('document');
    setChiliDoc(doc);
  };

  const refreshSelectedFrame = async () => {
    if (!publisher) return;
    const frame = await publisher.getObject('document.selectedFrame');
    setChiliSelectedFrame(frame);
  };

  const refreshSelectedText = async () => {
    if (!publisher && !chiliSelectedText) return;
    const selectedText = await publisher.getObject('document.selectedText.textFormat');
    setChiliSelectedText(selectedText);
  };

  const handleSaveDocument = (prevFunc = null) => {
    if (!prevFunc) dispatch(toggleSavingArtwork({ type: 'save', value: true }));
    return publisher.executeFunction('document', 'GetTempXML').then((result) => {
      return dispatch(
        saveDocument({
          document: result,
          params: {
            ...(currentArtwork && { id: currentArtwork.id }),
            ...(selectedProperty && { property: selectedProperty.id }),
          },
        })
      )
        .unwrap()
        .then((data) => {
          setCurrentArtwork(data);
          if (prevFunc) return data;
          dispatch(toggleSavingArtwork({ type: 'save', value: false }));
          enqueueSnackbar('Artwork saved.', { autoHideDuration: 3000 });
        })
        .catch((error) => {
          console.log(error);
          dispatch(toggleSavingArtwork({ type: 'save', value: false }));
          enqueueSnackbar('Error saving artwork', { variant: 'error' });
        });
    });
  };

  const handleExportDocument = (prevFunc = null) => {
    if (!prevFunc) dispatch(toggleSavingArtwork({ type: 'export', value: true }));
    return handleSaveDocument(handleExportDocument).then((data) => {
      return dispatch(exportDocument(data))
        .unwrap()
        .then((data) => {
          if (prevFunc) return data;
          dispatch(toggleSavingArtwork({ type: 'export', value: false }));
          enqueueSnackbar('Artwork exported.', { autoHideDuration: 3000 });
        })
        .catch((error) => {
          console.log(error);
          dispatch(toggleSavingArtwork({ type: 'export', value: false }));
          enqueueSnackbar('Error exporting artwork', { variant: 'error' });
        });
    });
  };

  const handleDownload = (document) => {
    dispatch(downloadArtworkPdf(document))
      .unwrap()
      .then(() => {
        dispatch(toggleSavingArtwork({ type: 'download', value: false }));
        enqueueSnackbar(
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {`${document.name} download successful.`}
          </span>
        );
      })
      .catch((error) => {
        dispatch(toggleSavingArtwork({ type: 'download', value: false }));
        enqueueSnackbar(error, { variant: 'error' });
      });
  };

  const pollRefreshDocument = (doc, attempt = 1) => {
    if (attempt > 3) {
      dispatch(toggleSavingArtwork({ type: 'download', value: false }));
      enqueueSnackbar('Request Timeout. Document not ready.', { variant: 'error' });
      return;
    }

    dispatch(refreshDocument(doc))
      .unwrap()
      .then((data) => {
        console.log('Polling attempt', attempt);
        if (data?.is_ready) {
          handleDownload(doc);
        } else {
          setTimeout(() => pollRefreshDocument(doc, attempt + 1), 2000);
        }
      })
      .catch(() => {
        dispatch(toggleSavingArtwork({ type: 'download', value: false }));
        enqueueSnackbar('Error fetching exported artwork', { variant: 'error' });
      });
  };

  const handleDownloadDocument = () => {
    dispatch(toggleSavingArtwork({ type: 'download', value: true }));
    handleExportDocument(handleDownloadDocument).then((document) => {
      if (document?.is_ready) {
        handleDownload(document);
      } else {
        pollRefreshDocument(document);
      }
    });
  };

  const handleSelectLayout = async (layout, closeFn) => {
    if (layout?.value) {
      await publisher.setProperty(`document.variables[Layout]`, 'value', layout.value);
      setTemplateLayout(layout);
      closeFn();
    }
  };

  const handleSelectFile = async (file) => {
    setLoadingSelectImage(true);
    const selectedFrame = await publisher.getObject('document.selectedFrame');

    if (file?.oid) {
      try {
        const imageUriSas = await dispatch(getFileUriSas({ id: file.oid })).unwrap();
        const exportedPdf = await dispatch(getExportedFile({ id: file.oid, params: { format: 'pdf' } })).unwrap();
        const pdfUriSas = await dispatch(getFileUriSas({ id: exportedPdf })).unwrap();

        const assetDefinitionXML =
          '<item id="' +
          file.id +
          '" ' +
          'name="' +
          file.name +
          '" ' +
          'remoteURL="' +
          imageUriSas +
          '" ' +
          'highResPdfURL="' +
          pdfUriSas +
          '" ' +
          'keepExternal="true" ' +
          'accessibleFromClient="true">' +
          '<fileInfo width="' +
          selectedFrame.pixelWidth +
          '" ' +
          'height="' +
          selectedFrame.pixelHeight +
          '" ' +
          'resolution="' +
          selectedFrame.realResolution +
          '" ' +
          'fileSize="280 kB" />' + //check how to get this
          '</item>';

        const selectedPageId = selectedFrame.page.substring(selectedFrame.page.indexOf('document.pages['));
        const framePath = `${selectedPageId}.frames[${selectedFrame.id}]`;

        await publisher.executeFunction(framePath, 'LoadContentFromExternalServerXmlString', assetDefinitionXML);

        await publisher.setProperty(framePath, 'fitMode', 'frame');
        await publisher.setProperty(framePath, 'variable', null);

        setLoadingSelectImage(false);
        refreshSelectedFrame();
      } catch (error) {
        setLoadingSelectImage(false);
        enqueueSnackbar('Failed to process asset', { variant: 'error' });
      }
    }
  };

  const handleRemoveFile = async () => {
    await publisher.executeFunction('document.selectedFrame', 'ClearContent');
    refreshSelectedFrame();
  };

  const handleCropFile = async (action, value) => {
    await publisher.setProperty('document.selectedFrame', action, value);
    refreshSelectedFrame();
  };

  const handleZoom = async (toggleZoom) => {
    if (!chiliDoc || !chiliDoc?.zoom) return;
    const currentZoom = Number(chiliDoc.zoom);

    let newZoom;

    if (toggleZoom === 'in') {
      newZoom = currentZoom + 25;
    } else if (toggleZoom === 'out') {
      newZoom = currentZoom - 25;
    } else if (!isNaN(Number(toggleZoom))) {
      newZoom = Number(toggleZoom);
    } else {
      return;
    }

    await publisher.setProperty('document', 'zoom', newZoom.toString());
    initializeChiliDoc();
  };

  const handleFit = async (fitOption) => {
    await publisher.executeFunction('document.editor', 'Fit', fitOption);
    const doc = await publisher.getObject('document');
    if (doc?.zoom) handleZoom(doc.zoom);
  };

  const handleUndo = async (undoOption) => {
    await publisher.executeFunction('document.undoManager', undoOption, 1);
    initializeChiliDoc();
  };

  const handleFrameOperations = async (action, value) => {
    //check if I can reuse this
    const frameOperations = {
      position: {
        actionName: 'index',
        values: {
          front: 5000,
          back: 0,
        },
      },
    };

    const operation = frameOperations?.[action];
    const val = operation?.values?.[value];

    if (!operation || val === undefined) {
      return;
    }

    await publisher.setProperty('document.selectedFrame', operation.actionName, val);
    initializeChiliDoc();
  };

  const handleDeleteFrame = async () => {
    await publisher.executeFunction('document.selectedFrame', 'Delete');
    initializeChiliDoc();
  };

  const updateFrameDimensions = async (action, value) => {
    if (chiliSelectedFrame?.hasContent === 'true' && action === 'displayHeight') {
      await publisher.setProperty('document.selectedFrame', action, chiliSelectedFrame?.displayHeight);
    } else await publisher.setProperty('document.selectedFrame', action, value);
    refreshSelectedFrame();
  };

  const addRGBColor = async (color) => {
    const newColor = await publisher.executeFunction('document.colors', 'Add'); //may need to handle cleanup in the future
    const colorPath = `document.colors[${newColor.id}]`;

    await publisher.setProperty(colorPath, 'type', 'RGB'); //all RGB for now
    await publisher.setProperty(colorPath, 'name', `Custom-${color.hex}`);
    await publisher.setProperty(colorPath, 'isDocumentColor', false);
    await publisher.setProperty(colorPath, 'r', color.rgb.r);
    await publisher.setProperty(colorPath, 'g', color.rgb.g);
    await publisher.setProperty(colorPath, 'b', color.rgb.b);
    return colorPath;
  };

  const updateFrameFillColor = async (color) => {
    if (color?.rgb && color?.hex) {
      const colorPath = await addRGBColor(color);

      await publisher.setProperty('document.selectedFrame', 'fillColor', `cp_object:${colorPath}`);
      await publisher.setProperty('document.selectedFrame', 'hasFill', true);
    }

    if (color === 'none') {
      await publisher.setProperty('document.selectedFrame', 'hasFill', false);
    }
  };

  const updateFrameBorder = async (property, value) => {
    if (!chiliSelectedFrame) return;
    switch (property) {
      case 'color':
        if (value?.rgb && value?.hex) {
          const colorPath = await addRGBColor(value);
          await publisher.setProperty('document.selectedFrame', 'borderColor', `cp_object:${colorPath}`);
          await publisher.setProperty('document.selectedFrame', 'hasBorder', true);
        }
        if (value === 'none') {
          await publisher.setProperty('document.selectedFrame', 'hasBorder', false);
          await publisher.setProperty('document.selectedFrame', 'borderStyle', 'normal');
          await publisher.setProperty('document.selectedFrame', 'borderWidth', 1);
          await publisher.setProperty('document.selectedFrame', 'borderCornerRadius', 0);
        }
        break;
      case 'style':
        await publisher.setProperty('document.selectedFrame', 'borderStyle', value);
        break;
      case 'width':
        await publisher.setProperty('document.selectedFrame', 'borderWidth', value);
        break;
      case 'radius':
        await publisher.setProperty('document.selectedFrame', 'borderCornerRadius', value);
        break;
    }
    refreshSelectedFrame();
  };

  const updateTextProperties = async (property, value) => {
    if (!chiliSelectedText) return;
    switch (property) {
      case 'color':
        if (value?.rgb && value?.hex) {
          const colorPath = await addRGBColor(value);
          await publisher.setProperty('document.selectedText.textFormat', 'color', `cp_object:${colorPath}`);
        }
        break;
      case 'size':
        await publisher.setProperty('document.selectedText.textFormat', 'fontSize', value);
        break;
      case 'align':
        await publisher.setProperty('document.selectedText.textFormat', 'textAlign', value);
        break;
      case 'font': {
        const fntTrade = await publisher.getObject(`document.fonts[${value}]`);
        await publisher.setProperty(
          'document.selectedText.textFormat',
          'font',
          'cp_object:document.fonts[' + fntTrade.id + ']'
        );
        break;
      }
    }
    refreshSelectedText();
  };

  useEffect(() => {
    setShowEditorTools(!!chiliSelectedFrame);
  }, [chiliSelectedFrame]);

  const callbacks = {
    handleDownloadDocument,
    handleExportDocument,
    handleSaveDocument,
    handleSelectFile,
    initializePublisher,
    setCurrentArtwork,
    setSelectedProperty,
    handleSelectLayout,
    //files
    setImageFiles,
    handleRemoveFile,
    handleCropFile,
    //text
    updateTextProperties,
    //toolbar functions
    handleFit,
    handleZoom,
    handleUndo,
    // frame functions
    handleFrameOperations,
    handleDeleteFrame,
    updateFrameDimensions,
    updateFrameFillColor,
    updateFrameBorder,
  };

  return (
    <ArtworkContext.Provider
      value={{
        currentArtwork,
        chiliDoc,
        chiliSelectedFrame,
        chiliSelectedText,
        documentRendered,
        imageFiles,
        loadingSelectImage,
        publisher,
        selectedProperty,
        showEditorTools,
        templateLayout,
        callbacks,
      }}
      {...props}
    >
      {children}
    </ArtworkContext.Provider>
  );
};
